import * as React from 'react';
import SearchFilterToggle from './SearchFilterToggle';
import { observer } from 'mobx-react-lite';
import { getStore } from '../store/store';
import loc from 'owa-localize';
import { searchFilterLabel_hasAttachment } from 'owa-locstrings/lib/strings/searchFilterLabel.locstring.json';
import { unreadFilter } from 'owa-locstrings/lib/strings/unreadfilter.locstring.json';
import TimeSearchFilter from './TimeSearchFilter';
import getSearchQueryHasAttachmentsKqlMatches from '../utils/getSearchQueryHasAttachmentsKqlMatches';
import { getScenarioStore, SearchScenarioId, startSearch } from 'owa-search';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { toMeFilter } from 'owa-locstrings/lib/strings/toMeFilter.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import isInteractiveFiltersEnabled from '../utils/isInteractiveFiltersEnabled';
import {
    toggleIsUnread,
    toggleIsToMe,
    toggleHasAttachments,
    toggleIsFlagged,
    toggleIsMentioned,
    setShouldShowAdvancedSearch,
} from '../actions/internalActions';
import { flaggedFilter } from 'owa-locstrings/lib/strings/flaggedfilter.locstring.json';
import { mentionsFilter } from 'owa-locstrings/lib/strings/mentionsFilter.locstring.json';
import { getAdvancedSearchButtonStyles } from './SearchFilterStyles';
import { advancedSearch } from './SearchFiltersContainer.locstring.json';
import { ActionButton } from '@fluentui/react/lib/Button';
import { logUsage } from 'owa-analytics';

enum SearchFilterType {
    Unread,
    HasAttachments,
    ToMe,
    Flagged,
    Mentioned,
}

export default observer(function SearchFiltersContainer(): JSX.Element {
    const mailSearchStore = getStore();
    const filters = [];

    const hasAttachmentsKqlMatch = useComputedValue(() => {
        const { searchText: searchBoxText } = getScenarioStore(SearchScenarioId.Mail);
        const matches = getSearchQueryHasAttachmentsKqlMatches(searchBoxText);
        let hasMatch = false;
        if (matches?.length > 0) {
            // Use last value if user has typed multiple kql values
            const value = matches.pop().substring(15 /* hasattachments: */).toLocaleLowerCase();
            hasMatch = value === 'yes' || value === 'true';
        }
        return hasMatch;
    });

    const onSearchFilterClick = React.useCallback((filterType: SearchFilterType) => {
        return () => {
            switch (filterType) {
                case SearchFilterType.Unread:
                    toggleIsUnread();
                    break;
                case SearchFilterType.HasAttachments:
                    toggleHasAttachments();
                    break;
                case SearchFilterType.ToMe:
                    toggleIsToMe();
                    break;
                case SearchFilterType.Flagged:
                    toggleIsFlagged();
                    break;
                case SearchFilterType.Mentioned:
                    toggleIsMentioned();
                    break;
            }
            startSearch('InteractiveFilter', SearchScenarioId.Mail, false /* explicitSearch */);
        };
    }, []);

    const onAdvancedSearchClick = (evt: React.MouseEvent<unknown>) => {
        logUsage('SearchFilter_AdvancedSearchClicked');
        setShouldShowAdvancedSearch(true);
    };

    if (isInteractiveFiltersEnabled()) {
        filters.push([
            <SearchFilterToggle
                label={loc(unreadFilter)}
                isSelected={mailSearchStore.isUnread}
                onClick={onSearchFilterClick(SearchFilterType.Unread)}
                key={'unread'}
            />,
            <TimeSearchFilter key={'time'} />,
        ]);
    }

    if (isFeatureEnabled('sea-interactiveFilters')) {
        filters.push([
            <SearchFilterToggle
                label={loc(searchFilterLabel_hasAttachment)}
                isSelected={hasAttachmentsKqlMatch || mailSearchStore.includeAttachments}
                onClick={onSearchFilterClick(SearchFilterType.HasAttachments)}
                key={'attachment'}
            />,
            <SearchFilterToggle
                label={loc(toMeFilter)}
                isSelected={mailSearchStore.isToMe}
                onClick={onSearchFilterClick(SearchFilterType.ToMe)}
                key={'toMe'}
            />,
            <SearchFilterToggle
                label={loc(flaggedFilter)}
                isSelected={mailSearchStore.isFlagged}
                onClick={onSearchFilterClick(SearchFilterType.Flagged)}
                key={'flagged'}
            />,
            <SearchFilterToggle
                label={loc(mentionsFilter)}
                isSelected={mailSearchStore.isMentioned}
                onClick={onSearchFilterClick(SearchFilterType.Mentioned)}
                key={'mentioned'}
            />,
            <ActionButton
                styles={getAdvancedSearchButtonStyles()}
                onClick={onAdvancedSearchClick}
                text={loc(advancedSearch)}
                key={'advancedsearch'}
            />,
        ]);
    }

    return <>{filters}</>;
});
